/**
 * ツールバー組み立て
 *
 * controls の部品を「ツールバーへ載せる」「押下状態を同期する」「ポップアップを
 * 開閉する」へ配線する。editor は実体でなく最小の interface で受ける
 *
 * 状態同期の購読リストはここに閉じ込め、外へは syncState() / syncStateAfterAction() だけ見せる
 */
import { createCommandButton, createDivider, createSelectBox, createTriggerButton } from './controls';
import { createSelectPopup } from './popups';
import type { RichTextSelectItem } from './config';

/** ツールバーが参照する editor の最小構造。isFocused は書き換えるので readonly にしない */
export interface ToolbarEditorLike {
  isFocused: boolean;
  commands: { focus(): void };
}

/** ツールバーが参照するポップアップ制御の最小構造 (createPopupController が満たす) */
export interface ToolbarPopupsLike {
  open(popup: HTMLElement, anchor: HTMLElement, className: string): void;
  close(): void;
  isOpen(className: string): boolean;
}

/** トリガーボタンの更新フック (brush が現在色をアイコンへ反映するのに使う) */
interface TriggerControl {
  setActive(active: boolean): void;
  setIconColors(colors: { color?: string | null; backgroundColor?: string | null }): void;
}

export const createToolbarBuilder = ({
  toolbarEl,
  editor,
  popups,
}: {
  toolbarEl: HTMLElement;
  editor: ToolbarEditorLike;
  popups: ToolbarPopupsLike;
}) => {
  const registered: (() => void)[] = [];

  const syncState = (): void => {
    for (const update of registered) update();
  };

  /**
   * ツールバー操作の直後に呼ぶ
   * Tiptap の focus は requestAnimationFrame 内でネイティブ focus() を呼ぶため、操作直後の
   * isFocused はまだ false のことがある (「フォントを選んでも反映されない時がある」の原因)。
   * ツールバー操作は明確なユーザー操作なので先に true にしてから同期する
   */
  const syncStateAfterAction = (): void => {
    editor.isFocused = true;
    syncState();
  };

  /**
   * 押下状態の同期を登録する
   * 未フォーカス時 (読み込み直後など) は「たまたまカーソルがある位置」の装飾を反映せず中立表示にする
   */
  const registerPressedSync = (control: { setActive(active: boolean): void }, isActive?: () => boolean): void => {
    if (isActive) registered.push(() => control.setActive(editor.isFocused && isActive()));
  };

  /** 開いている状態でもう一度押したら閉じる (トグル) */
  const togglePopup = (className: string, openPopup: () => void): void => {
    const wasOpen = popups.isOpen(className);
    popups.close();
    if (!wasOpen) openPopup();
  };

  return {
    syncState,
    syncStateAfterAction,

    /** コマンドトグルボタン (太字 / 斜体 / 下線 / 装飾解除 / リスト) */
    commandButton: ({
      iconName,
      label,
      isActive,
      onClick,
    }: {
      iconName: string;
      label: string;
      isActive?: () => boolean;
      onClick: () => void;
    }): HTMLElement => {
      const button = createCommandButton({
        iconName,
        label,
        onClick: () => {
          onClick();
          editor.commands.focus();
          syncStateAfterAction();
        },
      });
      toolbarEl.appendChild(button.el);
      registerPressedSync(button, isActive);
      return button.el;
    },

    /** ポップアップを開くボタン (文字色 / 段落 / 行の高さ / テーブル / リンク) */
    triggerButton: ({
      iconName,
      label,
      popupClass,
      openPopup,
      isActive,
      onUpdate,
    }: {
      iconName: string;
      label: string;
      /** トグル判定に使うポップアップのクラス */
      popupClass: string;
      openPopup(anchor: HTMLElement): void;
      isActive?: () => boolean;
      onUpdate?: (control: TriggerControl, isFocused: boolean) => void;
    }): HTMLElement => {
      const button = createTriggerButton({
        iconName,
        label,
        onClick: () => togglePopup(popupClass, () => openPopup(button.el)),
      });
      toolbarEl.appendChild(button.el);
      registerPressedSync(button, isActive);
      if (onUpdate) registered.push(() => onUpdate(button, editor.isFocused));
      return button.el;
    },

    /** 固定幅 SelectBox (フォント / 段落 / 文字サイズ)。選択肢は開くたびに作り直す */
    selectBox: ({
      ref,
      label,
      placeholder,
      items,
      getValue,
      onSelect,
      renderItem,
    }: {
      ref: string;
      /** 何を選ぶ欄かを示すツールチップ。選択済みの値を出す placeholder とは別物 */
      label: string;
      placeholder: string;
      items: readonly RichTextSelectItem[];
      getValue(): string;
      onSelect(value: string): void;
      renderItem(item: RichTextSelectItem): string;
    }): HTMLElement => {
      const openClass = 'codmon-rte__select-popup--' + ref;
      // 未フォーカス時は空文字 = どれも選択なし。ボタン表示と矛盾させないため
      const currentValue = (): string => (editor.isFocused ? getValue() : '');
      const box = createSelectBox({
        ref,
        label,
        onToggle: () =>
          togglePopup(openClass, () => {
            const popup = createSelectPopup({
              items,
              currentValue: currentValue(),
              renderItem,
              onSelect: (value) => {
                onSelect(value);
                popups.close();
                editor.commands.focus();
                syncStateAfterAction();
              },
            });
            popups.open(popup, box.el, 'codmon-rte__select-popup ' + openClass);
          }),
      });
      toolbarEl.appendChild(box.el);
      // 選択肢に無い値 (font 未確定など) は placeholder 表示
      registered.push(() => {
        const found = items.find((item) => item.value === currentValue());
        box.setLabel(found ? found.label : placeholder, !found);
      });
      return box.el;
    },

    /** ボタン群の区切り線 */
    divider: (): void => {
      toolbarEl.appendChild(createDivider());
    },
  };
};
